import React, { createContext, useContext, useState, useEffect } from 'react';

const DataContext = createContext(null);

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const [data, setData] = useState({
    projects: [],
    awards: [],
    exhibitions: [],
    research: [],
    bio: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const fetchOptions = { cache: 'no-store' };
        
        const [projectsRes, awardsRes, exhibitionsRes, researchRes, bioRes] = await Promise.all([
          fetch(`${baseURL}/api/projects`, fetchOptions),
          fetch(`${baseURL}/api/awards`, fetchOptions),
          fetch(`${baseURL}/api/exhibitions`, fetchOptions),
          fetch(`${baseURL}/api/research`, fetchOptions),
          fetch(`${baseURL}/api/bio`, fetchOptions),
        ]);

        if (!projectsRes.ok || !awardsRes.ok || !exhibitionsRes.ok || !researchRes.ok || !bioRes.ok) {
           throw new Error('Failed to fetch one or more endpoints');
        }

        const [projects, awards, exhibitions, research, bio] = await Promise.all([
          projectsRes.json(),
          awardsRes.json(),
          exhibitionsRes.json(),
          researchRes.json(),
          bioRes.json(),
        ]);

        setData({ projects, awards, exhibitions, research, bio });
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center space-y-4">
        <p className="text-red-500 font-mono text-sm">Failed to load data: {error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-black text-white text-xs uppercase tracking-widest font-bold hover:bg-neutral-800 transition-colors"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  return (
    <DataContext.Provider value={data}>
      {children}
    </DataContext.Provider>
  );
};
